import { useQueries, useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import {
	AlertTriangle,
	Building01,
	Building07,
	Check,
	GitMerge,
	HelpCircle,
	RefreshCw01,
	Send02,
	SlashCircle01,
} from "@untitledui/icons";
import { type CSSProperties, useRef, useState } from "react";
import type { Key } from "react-aria-components";
import { Button as AriaButton } from "react-aria-components";
import { DisplayInput } from "@/components/Atoms/DisplayField";
import { DisplayFieldShell } from "@/components/Atoms/DisplayField/DisplayFieldShell";
import {
	DisplaySelect,
	DisplaySelectBadgeVariant,
	DisplaySelectValueType,
} from "@/components/Atoms/DisplayField/DisplaySelect";
import {
	DisplayFieldFeedback,
	DisplayFieldState,
} from "@/components/Atoms/DisplayField/display-field.shared";
import { useFieldFeedbackMap } from "@/components/Atoms/FeedbackIndicator/use-field-feedback-map";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { PhoneNumber } from "@/components/common";
import { fieldLessThanLength } from "@/components/form/validation/create-common-validators";
import { ConfirmationModal } from "@/components/Organisms/ConfirmationModal";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useEntityActionAllowed } from "@/hooks/use-entity-action-allowed";
import {
	formatPhoneNumber,
	handlePhoneInputChange,
	phoneValueForStorage,
} from "@/lib/dynamicForms/utils/phoneFormatter.util";
import { authQueryOptions } from "@/services/auth";
import {
	deriveBelongsTo,
	type OrganizationRoleListItem,
	OrganizationStatusCode,
	organizationQueryOptions,
	organizationService,
	useForceOrganizationUserPasswordReset,
	useUpdateOrganizationUser,
	useUpdateOrganizationUserStatus,
} from "@/services/organization";
import { getUserInviteStatusName, USER_INVITE_STATUS } from "@/services/user";
import {
	useResendInvitation,
	useRevokeInvitation,
	userInviteQueries,
} from "@/services/user-invite";
import { formatId } from "@/utils/format-id";
import { showApiErrorToast, showSuccessToast } from "@/utils/toast.utils";
import { SearchSchema, statusColors } from "../../$orgUserId";
import {
	type BelongsToDestination,
	type BelongsToSaveDecision,
	belongsToCheckBody,
	belongsToDestinationFromKey,
	belongsToListsForRole,
	belongsToUpdateBody,
	resolveBelongsToSave,
	shouldCommitBelongsToSave,
} from "./belongs-to-save";

type TextField = "displayName" | "title";
type EditingField = TextField | "phone";
type FeedbackField = EditingField | "role" | "belongsTo";
type PendingBelongsTo = BelongsToDestination & {
	modal: Exclude<BelongsToSaveDecision, "ignore" | "save">;
};
const TEXT_FIELD_MAX_LENGTH = 100;

import { OrgMemberStatusActionModal } from "./org-member-status-action-modal";
import {
	ORG_MEMBER_STATUS_ACTIONS,
	type OrgMemberStatusAction,
	orgMemberStatusActionName,
	orgMemberStatusActionsFromPermissions,
	orgMemberStatusApiValue,
} from "./org-member-status-actions";
export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/users/(org-users)/$orgUserId/$userId/details",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

function RouteComponent() {
	const { t } = useAppTranslation();

	const revokeInvitation = useRevokeInvitation();
	const resendInvitation = useResendInvitation();
	// control the visibility of the resend and revoke buttons
	const [isResendOpen, setIsResendOpen] = useState(false);
	const [isRevokeOpen, setIsRevokeOpen] = useState(false);
	// control the visibility of the reset password modal
	const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
	const [pendingStatusAction, setPendingStatusAction] =
		useState<OrgMemberStatusAction | null>(null);
	const [removeEmail, setRemoveEmail] = useState("");
	// control the pending action
	const [pendingAction, setPendingAction] = useState<{
		type: "resend" | "revoke" | "resetPassword";
		userId?: string;
		inviteId?: string;
		organizationId?: string;
	} | null>(null);

	const forceOrganizationUserPasswordReset =
		useForceOrganizationUserPasswordReset();
	const updateOrganizationUser = useUpdateOrganizationUser();
	const updateOrganizationUserStatus = useUpdateOrganizationUserStatus();
	const { feedbackByField, setFieldFeedback } =
		useFieldFeedbackMap<FeedbackField>();
	const [editingField, setEditingField] = useState<EditingField | null>(null);
	const [draftByField, setDraftByField] = useState<
		Partial<Record<TextField, string>>
	>({});
	const [errorByField, setErrorByField] = useState<
		Partial<Record<TextField, string>>
	>({});
	const [phoneDraft, setPhoneDraft] = useState<string | undefined>(undefined);
	const [phoneError, setPhoneError] = useState<string | undefined>(undefined);
	const [belongsToMenuOpen, setBelongsToMenuOpen] = useState(false);
	const [belongsToMenuWidth, setBelongsToMenuWidth] = useState<number | null>(
		null,
	);
	const [pendingBelongsTo, setPendingBelongsTo] =
		useState<PendingBelongsTo | null>(null);
	const belongsToAnchorRef = useRef<HTMLDivElement>(null);

	// Modal handlers
	const handleResendInvitation = async () => {
		// Add your resend invitation mutation here
		resendInvitation.mutate(pendingAction?.inviteId ?? "");
		setIsResendOpen(false);
		setPendingAction(null);
	};

	const handleRevokeAccess = async () => {
		revokeInvitation.mutate(pendingAction?.inviteId ?? "");
		// Add your revoke access mutation here
		setIsRevokeOpen(false);
		setPendingAction(null);
	};

	const handleResetPassword = async () => {
		try {
			await forceOrganizationUserPasswordReset.mutateAsync({
				organizationId: params.orgId,
				organizationUserId: params.orgUserId,
			});
		} catch {
			return;
		}
		setIsResetPasswordOpen(false);
		setPendingAction(null);
	};

	const onResetPassword = () => {
		setIsResetPasswordOpen(true);
		setPendingAction({
			type: "resetPassword",
			userId: params.userId,
			organizationId: params.orgId,
		});
	};
	const onRevoke = () => {
		setIsRevokeOpen(true);
		setPendingAction({
			type: "revoke",
			userId: params.userId,
			organizationId: params.orgId,
			inviteId: userInvitesByUserId.data?.[0]?.id,
		});
	};
	const onResend = () => {
		setIsResendOpen(true);
		setPendingAction({
			type: "resend",
			userId: params.userId,
			organizationId: params.orgId,
			inviteId: userInvitesByUserId.data?.[0]?.id,
		});
	};

	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/users/(org-users)/$orgUserId/$userId/details",
	});
	// const { copy, copied } = useClipboard();
	const navigate = useNavigate();

	const [
		userById,
		organizationQuery,
		organizationRoles,
		userInvitesByUserId,
		adminUserCount,
	] = useQueries({
		queries: [
			organizationQueryOptions.users.detail(params.orgId, params.orgUserId),
			organizationQueryOptions.detail(params.orgId),
			organizationQueryOptions.roles.list(params.orgId),
			userInviteQueries.invites.list(1, 10, {
				UserId: params.userId,
				OrganizationId: params.orgId,
			}),
			organizationQueryOptions.users.count(params.orgId, { IsAdmin: true }),
		],
	});

	const canResendUserInvite = useQuery(
		authQueryOptions.checkAction({
			name: "ResendUserInvite",
			entityType: "UserInvite",
			entityId: userInvitesByUserId.data?.[0]?.id,
		}),
	);

	const canRevokeUserInvite = useQuery(
		authQueryOptions.checkAction({
			name: "RevokeUserInvite",
			entityType: "OrganizationUser",
			entityId: userInvitesByUserId.data?.[0]?.id,
		}),
	);

	const canForcePasswordReset = useQuery(
		authQueryOptions.checkAction({
			name: "ForcePasswordReset",
			entityType: "OrganizationUser",
			entityId: params.orgUserId,
		}),
	);

	const statusActionChecks = useQueries({
		queries: ORG_MEMBER_STATUS_ACTIONS.map((action) =>
			authQueryOptions.checkAction({
				name: orgMemberStatusActionName(action),
				entityType: "OrganizationUser",
				entityId: params.orgUserId,
			}),
		),
	});

	// const userInvitesByUserId = useQuery(
	// 	userInviteQueries.invites.list(1, 10, { UserId: params.userId }),
	// );

	// const userById = useQuery(
	// 	organizationQueryOptions.users.detail(params.orgId, params.userId),
	// );
	// const organizationQuery = useQuery(
	// 	organizationQueryOptions.detail(params.orgId),
	// );

	// const organizationRoles =  useQuery(organizationQueryOptions.roles.list(params.orgId));

	// ==================== ADMINISTRATOR PROTECTION LOGIC ====================
	// Check if current user's role is an admin role
	const currentUserRole = userById.data?.organizationRoles?.[0];
	const isCurrentUserAdmin = currentUserRole?.adminRole === true;

	const adminCount = adminUserCount.data ?? 0;

	// Check if organization is Active
	const isOrgActive =
		organizationQuery.data?.status?.code === OrganizationStatusCode.Active;

	// Loading state for protection data
	const isLoadingProtectionData =
		adminUserCount.isLoading ||
		organizationQuery.isLoading ||
		userById.isLoading;

	// Determine if this is the only admin in an active org
	const isOnlyAdminInActiveOrg =
		isOrgActive && isCurrentUserAdmin && adminCount === 1;

	// ==================== END ADMINISTRATOR PROTECTION LOGIC ====================

	// Derive invitation status from actual data
	const currentInviteStatus = userInvitesByUserId.data?.[0]?.statusCode as
		| USER_INVITE_STATUS
		| undefined;
	const isPendingInvitation =
		currentInviteStatus === USER_INVITE_STATUS.PENDING;

	const isExpiredInvitation =
		currentInviteStatus === USER_INVITE_STATUS.EXPIRED;

	// Show reset password only when NOT pending (user has already accepted invitation)
	const showResetPasswordSection =
		canForcePasswordReset.data &&
		currentInviteStatus !== undefined &&
		!isPendingInvitation &&
		!isExpiredInvitation;
	// Show revoke/resend invitation only when pending
	const showInvitationSection = isPendingInvitation || isExpiredInvitation;
	const membershipEdit = useEntityActionAllowed({
		name: "UpdateOrganizationUserInfo",
		entityType: "OrganizationUser",
		entityId: params.orgUserId,
	});
	const canEditMembership = membershipEdit.allowed && !membershipEdit.isPending;
	const canEditRole =
		canEditMembership && !isLoadingProtectionData && !isOnlyAdminInActiveOrg;

	const currentRoleId = currentUserRole?.id ?? "";
	const listedRole = organizationRoles.data?.find(
		(role) => role.id === currentRoleId,
	);
	const currentRoleDetail = useQuery({
		...organizationQueryOptions.systemOrganizationRoles.detail(currentRoleId),
		enabled:
			canEditMembership &&
			currentRoleId.length > 0 &&
			organizationRoles.isFetched &&
			!listedRole,
	});
	const belongsToLists = belongsToListsForRole(
		listedRole ?? currentRoleDetail.data,
	);
	const subOrganizations = useQuery({
		...organizationQueryOptions.subOrganizations.list(params.orgId, 1, 100),
		enabled: canEditMembership && belongsToLists.subOrganizations,
	});
	const subContractors = useQuery({
		...organizationQueryOptions.subContractors.list(params.orgId, 1, 100),
		enabled: canEditMembership && belongsToLists.subContractors,
	});

	const statusActions = orgMemberStatusActionsFromPermissions(
		Object.fromEntries(
			ORG_MEMBER_STATUS_ACTIONS.map((action, index) => [
				action,
				statusActionChecks[index]?.data === true,
			]),
		),
	);
	const memberName =
		[userById.data?.firstName, userById.data?.lastName]
			.filter((part) => part?.trim())
			.join(" ")
			.trim() ||
		userById.data?.displayName ||
		"";
	const statusName = userById.data?.status?.name ?? "";
	const statusColor =
		statusColors[statusName as keyof typeof statusColors] ?? "gray";

	const closeStatusAction = () => {
		if (updateOrganizationUserStatus.isPending) return;
		setPendingStatusAction(null);
		setRemoveEmail("");
	};

	const confirmStatusAction = async () => {
		if (!pendingStatusAction) return;
		const action = pendingStatusAction;
		try {
			await updateOrganizationUserStatus.mutateAsync({
				organizationId: params.orgId,
				organizationUserId: params.orgUserId,
				data: { status: orgMemberStatusApiValue(action) },
			});
			showSuccessToast({
				message: t(
					`org-admin.users.details.statusActions.toast.${
						action === "activate"
							? "activated"
							: action === "suspend"
								? "suspended"
								: action === "archive"
									? "archived"
									: "removed"
					}`,
					{ name: memberName },
				),
			});
			setPendingStatusAction(null);
			setRemoveEmail("");
			if (action === "remove" || action === "archive") {
				navigate({
					to: "/org-admin/$orgId/users",
					params: { orgId: params.orgId },
					replace: true,
				});
			}
		} catch (error) {
			showApiErrorToast({
				message: t("common.errors.unknownError"),
				error,
			});
		}
	};

	const textLabel = (field: TextField) =>
		t(field === "displayName" ? "User.DisplayName" : "OrganizationUser.Title", {
			useFieldInfo: true,
		});

	const serverText = (field: TextField) => userById.data?.[field] ?? "";

	const setTextError = (field: TextField, message: string | undefined) => {
		setErrorByField((prev) => ({ ...prev, [field]: message }));
	};

	const clearTextDraft = (field: TextField) => {
		setDraftByField((prev) => {
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	const beginTextEdit = (field: TextField) => {
		setTextError(field, undefined);
		setEditingField(field);
		setDraftByField((prev) => ({ ...prev, [field]: serverText(field) }));
	};

	const cancelTextEdit = (field: TextField) => {
		setTextError(field, undefined);
		setFieldFeedback(field, DisplayFieldFeedback.Idle);
		clearTextDraft(field);
		setEditingField((current) => (current === field ? null : current));
	};

	const commitText = async (field: TextField) => {
		const raw = draftByField[field];
		if (raw === undefined) {
			setEditingField((current) => (current === field ? null : current));
			return;
		}
		const next = raw.trim();
		if (next === serverText(field)) {
			cancelTextEdit(field);
			return;
		}
		if (next.length > TEXT_FIELD_MAX_LENGTH) {
			setTextError(
				field,
				fieldLessThanLength(t, textLabel(field), TEXT_FIELD_MAX_LENGTH),
			);
			return;
		}
		setEditingField((current) => (current === field ? null : current));
		setFieldFeedback(field, DisplayFieldFeedback.Saving);
		try {
			await updateOrganizationUser.mutateAsync({
				organizationId: params.orgId,
				organizationUserId: params.orgUserId,
				data: { [field]: next || null },
			});
			setTextError(field, undefined);
			clearTextDraft(field);
			setFieldFeedback(field, DisplayFieldFeedback.Saved);
		} catch (error) {
			setFieldFeedback(field, DisplayFieldFeedback.Idle);
			showApiErrorToast({
				message: t("common.errors.unknownError"),
				error,
			});
		}
	};

	const serverPhone = phoneValueForStorage(userById.data?.phone ?? "");

	const beginPhoneEdit = () => {
		setPhoneError(undefined);
		setEditingField("phone");
		setPhoneDraft(formatPhoneNumber(serverPhone));
	};

	const cancelPhoneEdit = () => {
		setPhoneError(undefined);
		setFieldFeedback("phone", DisplayFieldFeedback.Idle);
		setPhoneDraft(undefined);
		setEditingField((current) => (current === "phone" ? null : current));
	};

	const commitPhone = async () => {
		if (phoneDraft === undefined) {
			setEditingField((current) => (current === "phone" ? null : current));
			return;
		}
		const next = phoneValueForStorage(phoneDraft);
		if (next === serverPhone) {
			cancelPhoneEdit();
			return;
		}
		if (next.length !== 0 && next.length !== 10) {
			setPhoneError(t("common.validation.phoneOptionalTenDigits"));
			return;
		}
		setEditingField((current) => (current === "phone" ? null : current));
		setFieldFeedback("phone", DisplayFieldFeedback.Saving);
		try {
			await updateOrganizationUser.mutateAsync({
				organizationId: params.orgId,
				organizationUserId: params.orgUserId,
				data: { phone: next || null },
			});
			setPhoneError(undefined);
			setPhoneDraft(undefined);
			setFieldFeedback("phone", DisplayFieldFeedback.Saved);
		} catch (error) {
			setFieldFeedback("phone", DisplayFieldFeedback.Idle);
			showApiErrorToast({
				message: t("common.errors.unknownError"),
				error,
			});
		}
	};

	const saveRole = async (roleId: string) => {
		if (!roleId || roleId === userById.data?.organizationRoles?.[0]?.id) return;
		setFieldFeedback("role", DisplayFieldFeedback.Saving);
		try {
			await updateOrganizationUser.mutateAsync({
				organizationId: params.orgId,
				organizationUserId: params.orgUserId,
				data: { roleId },
			});
			setFieldFeedback("role", DisplayFieldFeedback.Saved);
		} catch (error) {
			setFieldFeedback("role", DisplayFieldFeedback.Idle);
			showApiErrorToast({
				message: t("common.errors.unknownError"),
				error,
			});
		}
	};

	const currentBelongsTo = userById.data
		? deriveBelongsTo(userById.data, {
				organization: organizationQuery.data,
			})
		: null;
	const currentBelongsToKey =
		currentBelongsTo?.type === "subContractor" && currentBelongsTo.id
			? `subcontractor:${currentBelongsTo.id}`
			: currentBelongsTo?.type === "subOrganization" && currentBelongsTo.id
				? `sub-organization:${currentBelongsTo.id}`
				: currentBelongsTo?.type === "organization"
					? "organization"
					: null;
	const currentBelongsToName =
		userById.data?.belongsTo || currentBelongsTo?.name || "";

	const saveBelongsTo = async (
		destination: BelongsToDestination,
	): Promise<boolean> => {
		setFieldFeedback("belongsTo", DisplayFieldFeedback.Saving);
		try {
			await updateOrganizationUser.mutateAsync({
				organizationId: params.orgId,
				organizationUserId: params.orgUserId,
				data: belongsToUpdateBody(destination),
			});
			setFieldFeedback("belongsTo", DisplayFieldFeedback.Saved);
			return true;
		} catch (error) {
			setFieldFeedback("belongsTo", DisplayFieldFeedback.Idle);
			showApiErrorToast({
				message: t("common.errors.unknownError"),
				error,
			});
			return false;
		}
	};

	const setBelongsToMenu = (open: boolean) => {
		if (open) {
			const width = belongsToAnchorRef.current?.offsetWidth;
			if (width) setBelongsToMenuWidth(width);
		}
		setBelongsToMenuOpen(open);
	};

	const chooseBelongsTo = async (key: Key) => {
		setBelongsToMenuOpen(false);
		const destination = belongsToDestinationFromKey(String(key));
		if (!destination || !currentBelongsToKey) return;
		const decision = resolveBelongsToSave({
			isCurrent: String(key) === currentBelongsToKey,
			acknowledgementRequired: false,
			destinationKind: destination.kind,
		});
		if (decision === "ignore") {
			return;
		}
		try {
			const acknowledgementRequired =
				await organizationService.checkForPotentialChangedAssignments(
					params.orgId,
					params.orgUserId,
					belongsToCheckBody(destination),
				);
			const next = resolveBelongsToSave({
				isCurrent: false,
				acknowledgementRequired,
				destinationKind: destination.kind,
			});
			if (shouldCommitBelongsToSave(next, false)) {
				await saveBelongsTo(destination);
				return;
			}
			if (next === "confirm-unassign" || next === "confirm-subcontractor") {
				setFieldFeedback("belongsTo", DisplayFieldFeedback.Idle);
				setPendingBelongsTo({ ...destination, modal: next });
			}
		} catch (error) {
			setFieldFeedback("belongsTo", DisplayFieldFeedback.Idle);
			showApiErrorToast({
				message: t("common.errors.unknownError"),
				error,
			});
		}
	};

	const confirmBelongsTo = async () => {
		if (!pendingBelongsTo) return;
		const destination = pendingBelongsTo;
		if (
			!shouldCommitBelongsToSave(
				destination.modal === "confirm-subcontractor"
					? "confirm-subcontractor"
					: "confirm-unassign",
				true,
			)
		) {
			return;
		}
		const saved = await saveBelongsTo(destination);
		if (saved) setPendingBelongsTo(null);
	};

	const cancelBelongsTo = () => {
		setPendingBelongsTo(null);
		setFieldFeedback("belongsTo", DisplayFieldFeedback.Idle);
	};

	const textField = (field: TextField) => {
		const isEditing = editingField === field;
		const draft = draftByField[field] ?? serverText(field);
		const error = errorByField[field];
		return (
			<DisplayInput
				label={textLabel(field)}
				data-testid={field === "title" ? "org-user-details-title" : undefined}
				value={isEditing ? draft : serverText(field) || undefined}
				state={
					isEditing
						? DisplayFieldState.Active
						: error
							? DisplayFieldState.Error
							: DisplayFieldState.Enabled
				}
				feedback={feedbackByField[field] ?? DisplayFieldFeedback.Idle}
				hintText={error}
				showHintText={Boolean(error)}
				showEdit={canEditMembership}
				editValue={isEditing ? draft : undefined}
				onEdit={canEditMembership ? () => beginTextEdit(field) : undefined}
				onEditChange={(value) => {
					setDraftByField((prev) => ({ ...prev, [field]: value }));
					setTextError(field, undefined);
				}}
				onEditCommit={() => void commitText(field)}
				onEditCancel={() => cancelTextEdit(field)}
			/>
		);
	};

	const isEditingPhone = editingField === "phone";
	const currentRole = userById.data?.organizationRoles?.[0];

	return (
		<div className="flex w-full flex-col">
			<DisplayInput
				label={t("User.FirstName", { useFieldInfo: true })}
				value={userById.data?.firstName}
				showEdit={false}
			/>
			<DisplayInput
				label={t("User.LastName", { useFieldInfo: true })}
				value={userById.data?.lastName}
				showEdit={false}
			/>
			{textField("displayName")}
			{isOnlyAdminInActiveOrg &&
			canEditMembership &&
			!isLoadingProtectionData ? (
				<DisplayFieldShell
					label={t("org-admin.users.details.belongsTo")}
					state={DisplayFieldState.Enabled}
					isPopulated={Boolean(userById.data?.belongsTo)}
					showEdit={false}
					data-testid="org-user-details-belongs-to"
					trailing={
						<Tooltip
							title={t(
								"org-admin.users.details.onlyAdministrator.belongsToTitle",
							)}
							description={t(
								"org-admin.users.details.onlyAdministrator.belongsToDescription",
							)}
							placement="top"
						>
							<TooltipTrigger
								aria-label={t(
									"org-admin.users.details.onlyAdministrator.belongsToTitle",
								)}
								className="flex size-[21px] cursor-pointer items-center justify-center text-fg-quaternary outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
							>
								<HelpCircle className="size-5" />
							</TooltipTrigger>
						</Tooltip>
					}
				>
					{userById.data?.belongsTo ? (
						<p className="w-full truncate font-display text-sm font-extrabold text-primary">
							{userById.data.belongsTo}
						</p>
					) : null}
				</DisplayFieldShell>
			) : (
				<div ref={belongsToAnchorRef} className="relative">
					<DisplayInput
						label={t("org-admin.users.details.belongsTo")}
						data-testid="org-user-details-belongs-to"
						value={userById.data?.belongsTo}
						feedback={feedbackByField.belongsTo ?? DisplayFieldFeedback.Idle}
						showEdit={
							canEditMembership &&
							!isLoadingProtectionData &&
							!isOnlyAdminInActiveOrg
						}
						onEdit={
							canEditMembership &&
							!isLoadingProtectionData &&
							!isOnlyAdminInActiveOrg
								? () => setBelongsToMenu(true)
								: undefined
						}
					/>
					{canEditMembership &&
					!isLoadingProtectionData &&
					!isOnlyAdminInActiveOrg ? (
						<Dropdown.Root
							isOpen={belongsToMenuOpen}
							onOpenChange={setBelongsToMenu}
						>
							<AriaButton
								aria-label={t("org-admin.users.details.belongsTo")}
								className="sr-only"
							/>
							<Dropdown.Popover
								triggerRef={belongsToAnchorRef}
								placement="bottom start"
								className="!w-(--trigger-width)"
								style={
									{
										"--trigger-width": `${belongsToMenuWidth ?? 320}px`,
									} as CSSProperties
								}
							>
								<Dropdown.Menu
									aria-label={t("org-admin.users.details.belongsTo")}
									className="max-h-[400px]"
									selectedKeys={
										currentBelongsToKey ? [currentBelongsToKey] : []
									}
									onAction={(key) => void chooseBelongsTo(key)}
								>
									<Dropdown.Section>
										<Dropdown.SectionHeader
											title={t("org-admin.users.details.belongsToMenu.current")}
											order="top"
										/>
										{currentBelongsToKey ? (
											<BelongsToMenuItem
												itemKey={currentBelongsToKey}
												label={currentBelongsToName}
												kind={
													currentBelongsTo?.type === "subContractor"
														? "subContractor"
														: currentBelongsTo?.type === "subOrganization"
															? "subOrganization"
															: "organization"
												}
												selected
											/>
										) : null}
									</Dropdown.Section>
									<Dropdown.Section>
										<Dropdown.SectionHeader
											title={t("org-admin.users.details.belongsToMenu.moveTo", {
												name: currentBelongsToName,
											})}
											order="below"
										/>
										{currentBelongsToKey !== "organization" &&
										organizationQuery.data ? (
											<BelongsToMenuItem
												itemKey="organization"
												label={organizationQuery.data.name ?? ""}
												kind="organization"
											/>
										) : null}
										{belongsToLists.subOrganizations
											? subOrganizations.data?.map((subOrganization) =>
													subOrganization.id &&
													currentBelongsToKey !==
														`sub-organization:${subOrganization.id}` ? (
														<BelongsToMenuItem
															key={subOrganization.id}
															itemKey={`sub-organization:${subOrganization.id}`}
															label={subOrganization.name ?? ""}
															kind="subOrganization"
														/>
													) : null,
												)
											: null}
										{belongsToLists.subContractors
											? subContractors.data?.map((subContractor) =>
													subContractor.id &&
													currentBelongsToKey !==
														`subcontractor:${subContractor.id}` ? (
														<BelongsToMenuItem
															key={subContractor.id}
															itemKey={`subcontractor:${subContractor.id}`}
															label={subContractor.name ?? ""}
															kind="subContractor"
														/>
													) : null,
												)
											: null}
									</Dropdown.Section>
								</Dropdown.Menu>
							</Dropdown.Popover>
						</Dropdown.Root>
					) : null}
				</div>
			)}
			{textField("title")}
			<DisplaySelect
				label={t("User.Roles", { useFieldInfo: true })}
				value={currentRole?.name}
				valueType={DisplaySelectValueType.Text}
				showValueIcon={false}
				showEdit={canEditRole && (organizationRoles.data?.length ?? 0) > 0}
				options={
					canEditRole
						? organizationRoles.data?.map((role: OrganizationRoleListItem) => ({
								id: String(role.id),
								label: role.name,
							}))
						: undefined
				}
				selectedKey={currentRole ? String(currentRole.id) : undefined}
				feedback={feedbackByField.role ?? DisplayFieldFeedback.Idle}
				onSelectionChange={(key) => void saveRole(key)}
			/>
			<DisplayInput
				label={t("User.Email", { useFieldInfo: true })}
				value={userById.data?.email}
				showEdit={false}
			/>
			<DisplayInput
				label={t("OrganizationUser.Phone", { useFieldInfo: true })}
				value={
					isEditingPhone ? (
						(phoneDraft ?? "")
					) : userById.data?.phone ? (
						<PhoneNumber phoneNumber={userById.data.phone} />
					) : undefined
				}
				state={
					isEditingPhone
						? DisplayFieldState.Active
						: phoneError
							? DisplayFieldState.Error
							: DisplayFieldState.Enabled
				}
				feedback={feedbackByField.phone ?? DisplayFieldFeedback.Idle}
				hintText={phoneError}
				showHintText={Boolean(phoneError)}
				showEdit={canEditMembership}
				editValue={isEditingPhone ? (phoneDraft ?? "") : undefined}
				onEdit={canEditMembership ? beginPhoneEdit : undefined}
				onEditChange={(value) => {
					setPhoneDraft(handlePhoneInputChange(value));
					setPhoneError(undefined);
				}}
				onEditCommit={() => void commitPhone()}
				onEditCancel={cancelPhoneEdit}
			/>
			<DisplaySelect
				label={t("org-admin.users.details.orgMemberStatus")}
				data-testid="org-user-status-field"
				value={statusName || undefined}
				valueType={DisplaySelectValueType.Badge}
				badgeVariant={DisplaySelectBadgeVariant.Dot}
				showValueIcon={false}
				badgeColor={statusColor}
				showEdit={statusActions.length > 0}
				options={
					statusActions.length > 0
						? statusActions.map((action) => ({
								id: action,
								label: t(
									`org-admin.users.details.statusActions.${action}.action`,
								),
								testId: `org-user-status-action-${action}`,
							}))
						: undefined
				}
				onSelectionChange={(key) => {
					setRemoveEmail("");
					setPendingStatusAction(key as OrgMemberStatusAction);
				}}
			/>
			<div className="relative">
				<DisplayInput
					label={t("org-admin.users.details.orgMemberInviteStatus")}
					value={
						userInvitesByUserId.data?.length
							? getUserInviteStatusName(
									userInvitesByUserId.data[0]?.statusCode as USER_INVITE_STATUS,
								)
							: undefined
					}
					showEdit={false}
				/>
				{showInvitationSection && currentInviteStatus ? (
					<div className="pointer-events-none absolute inset-y-0 right-4 flex items-center gap-2">
						<div className="pointer-events-auto flex items-center gap-2">
							{canRevokeUserInvite.data &&
							currentInviteStatus !== USER_INVITE_STATUS.EXPIRED ? (
								<Button
									color="secondary"
									size="sm"
									data-testid="org-user-invite-revoke-btn"
									onClick={onRevoke}
								>
									{t("org-admin.users.details.organizations.buttons.revoke")}
								</Button>
							) : null}
							{canResendUserInvite.data ? (
								<Button
									color="secondary"
									size="sm"
									data-testid="org-user-invite-resend-btn"
									onClick={onResend}
								>
									{t("org-admin.users.details.organizations.buttons.resend")}
								</Button>
							) : null}
						</div>
					</div>
				) : null}
			</div>
			<DisplayInput
				label={t("OrganizationUser.Id", { useFieldInfo: true })}
				value={formatId(userById.data?.id) || undefined}
				copyable
				copyText={userById.data?.id}
				showEdit={false}
			/>
			{/* Reset Password Section - Only show when NOT pending (user has accepted invitation) */}
			{showResetPasswordSection && (
				<div className="bg-gray-50 border-t border-gray-200 border-solid px-6 py-4 pl-0 pr-0">
					<div className="flex items-center justify-between">
						{/* Invitation Status Badge */}
						<div className="flex items-center gap-2"></div>

						{/* Action Buttons */}
						<div className="flex items-center gap-3">
							{onResetPassword && (
								<Button
									color="secondary"
									size="sm"
									data-testid="org-user-password-reset-btn"
									onClick={onResetPassword}
								>
									{t(
										"admin.users.tabs.organization.details.organizations.forcePasswordReset.title",
									)}
								</Button>
							)}
						</div>
					</div>
				</div>
			)}
			<ConfirmationModal
				isOpen={pendingBelongsTo !== null}
				setIsOpen={(open) => {
					if (!open) cancelBelongsTo();
				}}
				title={
					pendingBelongsTo?.modal === "confirm-subcontractor"
						? t("org-admin.users.details.belongsToChangeSubcontractor.title")
						: t("org-admin.users.details.belongsToUnassign.title")
				}
				description={
					pendingBelongsTo?.modal === "confirm-subcontractor"
						? t(
								"org-admin.users.details.belongsToChangeSubcontractor.description",
								{ name: currentBelongsToName },
							)
						: t("org-admin.users.details.belongsToUnassign.description", {
								name: currentBelongsToName,
							})
				}
				icon={() => <AlertTriangle className="text-warning-600" size={32} />}
				iconBgColor="!bg-warning-100"
				confirmText={t("org-admin.users.details.belongsToConfirm")}
				cancelText={t("common.actions.cancel")}
				onConfirm={confirmBelongsTo}
				onCancel={cancelBelongsTo}
				isLoading={updateOrganizationUser.isPending}
				confirmTestId="org-user-belongs-to-confirm"
			/>
			{/* Modals */}
			<ConfirmationModal
				isOpen={isResendOpen}
				setIsOpen={setIsResendOpen}
				title={t(
					"org-admin.users.details.organizations.resendInvitation.title",
				)}
				description={t(
					"org-admin.users.details.organizations.resendInvitation.description",
				)}
				icon={() => <Send02 className="text-brand-500" size={32} />}
				iconBgColor="!bg-[var(--color-bg-brand-primary)]"
				confirmText={t(
					"org-admin.users.details.organizations.resendInvitation.confirmText",
				)}
				cancelText={t("common.actions.cancel")}
				onConfirm={handleResendInvitation}
				onCancel={() => {
					setIsResendOpen(false);
					setPendingAction(null);
				}}
				confirmColor="primary"
				confirmTestId="org-user-invite-resend-confirm-btn"
			/>

			<ConfirmationModal
				isOpen={isRevokeOpen}
				setIsOpen={setIsRevokeOpen}
				title={t(
					"org-admin.users.details.organizations.revokeInvitation.title",
				)}
				description={t(
					"org-admin.users.details.organizations.revokeInvitation.description",
				)}
				icon={() => <SlashCircle01 className="text-brand-500" size={32} />}
				iconBgColor="!bg-[var(--color-bg-brand-primary)]"
				confirmText={t(
					"org-admin.users.details.organizations.revokeInvitation.confirmText",
				)}
				cancelText={t("common.actions.cancel")}
				onConfirm={handleRevokeAccess}
				onCancel={() => {
					setIsRevokeOpen(false);
					setPendingAction(null);
				}}
				confirmColor="primary"
				confirmTestId="org-user-invite-revoke-confirm-btn"
			/>

			<ConfirmationModal
				isOpen={isResetPasswordOpen}
				setIsOpen={setIsResetPasswordOpen}
				title={t(
					"admin.users.tabs.organization.details.organizations.forcePasswordReset.title",
				)}
				description={t(
					"admin.users.tabs.organization.details.organizations.forcePasswordReset.description",
				)}
				icon={() => <RefreshCw01 className="text-brand-500" size={32} />}
				iconBgColor="!bg-[var(--color-bg-brand-primary)]"
				confirmText={t(
					"admin.users.tabs.organization.details.organizations.resetPassword.confirmText",
				)}
				cancelText={t("common.actions.cancel")}
				onConfirm={handleResetPassword}
				onCancel={() => {
					setIsResetPasswordOpen(false);
					setPendingAction(null);
				}}
				confirmColor="primary"
				confirmTestId="org-user-password-reset-confirm-btn"
			/>
			<OrgMemberStatusActionModal
				isOpen={pendingStatusAction != null}
				variant={pendingStatusAction ?? "suspend"}
				email={removeEmail}
				userEmail={userById.data?.email ?? ""}
				userName={memberName}
				isLoading={updateOrganizationUserStatus.isPending}
				onEmailChange={setRemoveEmail}
				onConfirm={confirmStatusAction}
				onCancel={closeStatusAction}
			/>
		</div>
	);
}

function BelongsToMenuItem({
	itemKey,
	label,
	kind,
	selected = false,
}: {
	itemKey: string;
	label: string;
	kind: BelongsToDestination["kind"];
	selected?: boolean;
}) {
	const Icon =
		kind === "subContractor"
			? GitMerge
			: kind === "subOrganization"
				? Building01
				: Building07;
	return (
		<Dropdown.Item id={itemKey} textValue={label || itemKey} unstyled>
			<div
				className={
					selected
						? "flex h-9 items-center gap-2 rounded-md bg-[rgba(181,71,8,0.02)] px-3"
						: "flex h-9 items-center gap-2 rounded-md px-3"
				}
			>
				<Icon aria-hidden className="size-5 shrink-0 text-fg-quaternary" />
				<span className="min-w-0 flex-1 truncate text-sm font-semibold text-secondary">
					{label}
				</span>
				{selected ? (
					<Check aria-hidden className="size-4 shrink-0 text-fg-quaternary" />
				) : null}
			</div>
		</Dropdown.Item>
	);
}
